import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewChild,
    inject
} from "@angular/core";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { ButtonComponent } from "@components/button/button.component";
import { TableComponent, TableComponent as TableComponent_1 } from "@components/table/table.component";
import { cloneDeep } from "lodash";
import { Subject, forkJoin, from, of } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { RaskColumn, RaskColumnButtonTypes } from "~app/common/components/table/models/rask-column";
import { RaskDataChanged } from "~app/common/components/table/models/rask-data-changed";

import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { AreaSelectionMarknadsomrade } from "~app/common/models/areaSelector";
import {
    Anskaffningsformer,
    Avverkningsformer,
    Forstaledskontraktstyp,
    RaskRoller,
    Transportslag
} from "~app/common/models/constants";
import { LanDTO } from "~app/common/models/distrikt/LanDTO";
import { ISelectValue } from "~app/common/models/selectValueModel";
import { BefraktareDto } from "~app/common/models/styrtabeller/BefraktareDto";
import { MottagningsplatsDTO } from "~app/common/models/styrtabeller/MottagningsplatsDTO";
import { RedovisningshanvisningDto } from "~app/common/models/styrtabeller/RedovisningshanvisningDto";
import { StyrtabellRedovisningshanvisningDto } from "~app/common/models/styrtabeller/StyrtabellRedovisningshanvisningDto";
import { StyrtabellVerksamhetsomradeDTO } from "~app/common/models/styrtabeller/StyrtabellVerksamhetsomradeDTO";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { CommandManager, ICommandManager } from "~app/common/services/cqs/commandManager";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { AddOrUpdateRedovisningshanvisningStyrningCommand } from "~app/features/administration/regelverk-viol3/command/AddOrUpdateRedovisningshanvisningStyrningCommand";
import { DeleteRedovisningshanvisningStyrningCommand } from "~app/features/administration/regelverk-viol3/command/DeleteRedovisningshanvisningStyrningCommand";
import { ISearchHandelssortimentModel } from "~app/features/forsaljning/handelssortiment/handelssortiment.models";
import { HandelssortimentSearchService } from "~app/features/forsaljning/handelssortiment/search-panel/handelssortiment-search.service";
import { RegelverkViolService, Vardegrupper } from "~app/features/regelverk-viol/regelverk-viol.service";
import { Viol3formatService } from "~app/features/shared/services/viol3format.service";
import { StyrtabellRedovisningshanvisningHandelssortimentDto, StyrtabellRedovisningshanvisningViewModel } from "../regelverkViol3.models";
import { RegelverkViol3Service } from "../regelverkViol3.service";
import { RedovisningshanvisningExtraDialogComponent } from "./redovisningshanvisning-extra-dialog/redovisningshanvisning-extra-dialog.component";
import { RedovisningshanvisningValidation } from "./redovisningshanvisning.validation";

@Component({
    selector: "rask-redovisningshanvisning",
    templateUrl: "./redovisningshanvisning.component.html",
    styleUrls: ["./redovisningshanvisning.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ActionButtonComponent,
        TableComponent_1,
        ButtonComponent
    ]
})
export class RedovisningshanvisningComponent implements OnInit, OnChanges {
    affarsformer: ISelectValue[];
    befraktare: BefraktareDto[];
    colDef: RaskColumn[];
    dataLoaded = false;
    handelssortiment: ISearchHandelssortimentModel[];
    lkd: LanDTO[];
    mottagningsplatser: MottagningsplatsDTO[];
    redovisningshanvisningar: RedovisningshanvisningDto[];
    @Input() searchResult: StyrtabellRedovisningshanvisningViewModel[];
    @ViewChild(TableComponent) tableComp: TableComponent;
    validationContext = RedovisningshanvisningValidation.init();
    verksamhetsomraden: AreaSelectionMarknadsomrade[];
    protected hasRoleProduktAdmin = false;
    private readonly busyService = inject(BusyService);
    private readonly commandManager: ICommandManager;
    private readonly commandService = inject(CommandService);
    private readonly handelssortimentSearchService = inject(HandelssortimentSearchService);
    private readonly raskModalService = inject(RaskModalService);
    private readonly regelverkService = inject(RegelverkViolService);
    private readonly regelverkViol3Service = inject(RegelverkViol3Service);

    private readonly authenticationService = inject(AuthenticationService);

    constructor() {
        this.commandManager = new CommandManager();
        this.hasRoleProduktAdmin = this.authenticationService.hasRole(RaskRoller.role.Produktadministrera);
    }

    add() {
        const newRow = new StyrtabellRedovisningshanvisningViewModel(new StyrtabellRedovisningshanvisningDto(), this.verksamhetsomraden, this.lkd);
        newRow.guid = GuidUtils.newGuid();
        newRow.anskaffningsform = [];
        newRow.affarsform = [];
        this.addOrUpdateCommand(newRow);

        this.searchResult.push(newRow);
        this.tableComp.refreshGrid();
    }

    canSave() {
        return this.commandManager.isDirty() && !this.validationContext.hasErrors(ValidationScopes.spara);
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.commandManager.clear();
    }

    ngOnInit(): void {
        forkJoin({
            motagningsplatser: this.regelverkViol3Service.getMottagningsplatser(),
            redovisningshanvisningar: this.regelverkViol3Service.getRedovisningshanvisningar(),
            handelssortiment: this.handelssortimentSearchService.search({}),
            affarsformer: this.regelverkService.getVarden(Vardegrupper.affarsform),
            marknadsomraden: this.regelverkViol3Service.getMarknadsOmraden(),
            befraktare: this.regelverkViol3Service.getBefraktare(),
            lkd: this.regelverkViol3Service.getLkd()
        }).subscribe(values => {
            this.mottagningsplatser = values.motagningsplatser;
            this.redovisningshanvisningar = values.redovisningshanvisningar;
            this.affarsformer = values.affarsformer;
            this.verksamhetsomraden = values.marknadsomraden;
            this.befraktare = values.befraktare;
            this.lkd = values.lkd;
            const dataChanged = new Subject<RaskDataChanged>();
            dataChanged.subscribe(x => {
                if (x.field === "anskaffningsform" && !x.model.anskaffningsform?.some((y: string) => Anskaffningsformer.isVirkesKop(y)) && x.model.affarsform?.length > 0) {
                    x.model.affarsform = [];
                    this.tableComp.gridApi.redrawRows();
                }
                if (x.field === "ersattningsrad" && x.model.ersattningsrad) {
                    x.model.redovisningshanvisning = undefined;
                    this.tableComp.gridApi.redrawRows();
                }
                this.update(x.model);
            });
            this.setupColumnDef(dataChanged);
            this.dataLoaded = true;
        });
    }

    save() {
        if (!this.canSave()) {
            return;
        }

        const work = from(this.commandService.pushCommands(this.commandManager));
        this.busyService.addWork(work)
            .subscribe(() => {
                this.commandManager.clear();
            });
    }

    // eslint-disable-next-line sonarjs/function-return-type
    treeSelectorViewFormatter = (arg) => {
        const nodes = [];
        if (ObjectUtils.isNullOrUndefined(arg)) {
            return new Set();
        }

        arg.forEach((mo: { selected: any; text: any; partialSelected: any; children: any[] }) => {
            if (mo.selected) {
                nodes.push(mo.text);
            } else if (mo.partialSelected) {
                mo.children?.forEach((ro: { selected: any; text: any; partialSelected: any; children: any[] }) => {
                    if (ro.selected) {
                        nodes.push(ro.text);
                    } else if (ro.partialSelected) {
                        ro.children?.forEach((po: { selected: any; text: any }) => {
                            if (po.selected) {
                                nodes.push(po.text);
                            }
                        });
                    }
                });
            }
        });
        return [...new Set(nodes)].join(",");
    };

    update(viewModel: StyrtabellRedovisningshanvisningViewModel) {
        this.addOrUpdateCommand(viewModel);
    }

    private addOrUpdateCommand(data: StyrtabellRedovisningshanvisningViewModel): AddOrUpdateRedovisningshanvisningStyrningCommand {
        let c = new AddOrUpdateRedovisningshanvisningStyrningCommand((c) => c.guid = data.guid);
        const existingCommand = this.commandManager.getCommand(c);
        if (!existingCommand) {
            if (!data.id) {
                c.markAsInsert();
            }
            this.commandManager.updateCommand(c);
        }

        c = existingCommand || c;
        c.id = data.id;
        c.guid = data.guid;
        c.ersatt = data.ersattningsrad;
        c.inmatningshandelssortiment = data.inmatningshandelssortiment;
        c.befraktareId = data.befraktare?.befraktareId;
        c.mottagningsplatsId = data.mottagningsplats?.id;
        c.anskaffningsform = data.anskaffningsform;
        c.avverkningsform = data.avverkningsform?.kod;
        c.affarsform = data.affarsform;
        c.transportslag = data.transportslag;
        c.kontraktstyp = data.kontraktstyp;
        c.redovisningshanvisningar = data.styrtabellRedovisningshanvisningar?.map(x => (
            {
                guid: x.guid,
                inmattHandelssortiment: x.inmatningshandelssortiment,
                redovisningshanvisningId: x.redovisningshanvisning?.id,
                ersatt: false
            }));
        c.redovisningshanvisningar.push({
            guid: data.guid,
            redovisningshanvisningId: data.redovisningshanvisning?.id,
            inmattHandelssortiment: null,
            ersatt: data.ersattningsrad
        });
        c.verksamhetsomraden = this.getSelectedVerksamhetsomraden(data.treeselectorVerksamhetsomradeSelectItems);
        c.lanKommunDistrikt = this.regelverkViol3Service.getSelectedLkd(data.treeselectorLkdSelectItems);
        return c;
    }

    private copy(rh: StyrtabellRedovisningshanvisningViewModel) {
        const newData = cloneDeep(rh);
        newData.id = undefined;
        newData.guid = GuidUtils.newGuid();
        newData.styrtabellRedovisningshanvisningar.every(x => x.guid = GuidUtils.newGuid());
        this.addOrUpdateCommand(newData);
        this.searchResult.push(newData);
        this.tableComp.refreshGrid();
    }

    private editExtraHsRh(rh: StyrtabellRedovisningshanvisningViewModel): void {
        const ref = this.raskModalService.openAsync<RedovisningshanvisningExtraDialogComponent>(
            "Extra HS RH",
            RedovisningshanvisningExtraDialogComponent,
            "md",
            true,
            { rh: rh,
                allrh: this.redovisningshanvisningar });
        ref.subscribe({
            next: result => {
                if (ObjectUtils.hasValue(result)) {
                    rh.styrtabellRedovisningshanvisningar = result;
                    this.update(rh);
                    this.tableComp.refreshGrid();
                }
            },
            error: () => {
                //
            }
        });
    }

    private getSelectedVerksamhetsomraden(selectionItems: SelectionItem[]): StyrtabellVerksamhetsomradeDTO[] {
        const selectedValues = this.regelverkService.getSelectedVerksamhetsomraden(selectionItems);
        return this.regelverkService.mapToSelectedVerksamhetsomrade(selectedValues, this.verksamhetsomraden);
    }

    private remove(rh: StyrtabellRedovisningshanvisningViewModel) {
        const index = this.searchResult.findIndex(obj => obj.guid === rh.guid);
        if (index > -1) {
            this.searchResult.splice(index, 1);
        }

        const cmd = new DeleteRedovisningshanvisningStyrningCommand(c => {
            c.guid = rh.guid;
        });
        this.commandManager.updateCommand(cmd);
        this.tableComp.refreshGrid();
    }

    private setupColumnDef(dataChanged: Subject<RaskDataChanged>) {
        this.colDef = [
            {
                editable: false,
                field: " ",
                type: "button",
                onClick: (rh: StyrtabellRedovisningshanvisningViewModel) => this.remove(rh),
                filter: false,
                text: "",
                minWidth: 30,
                maxWidth: 30,
                disabled: !this.hasRoleProduktAdmin,
                buttonType: RaskColumnButtonTypes.delete
            },
            {
                editable: false,
                field: "  ",
                type: "button",
                onClick: (rh: StyrtabellRedovisningshanvisningViewModel) => this.copy(rh),
                filter: false,
                text: "",
                minWidth: 30,
                maxWidth: 30,
                disabled: !this.hasRoleProduktAdmin,
                buttonType: RaskColumnButtonTypes.copy
            },
            {
                headerName: "Inmätningshandelssortiment *",
                field: "inmatningshandelssortiment",
                filter: true,
                filterParams: {
                    filterOptions: ["contains"],
                    maxNumConditions: 1
                },
                editable: this.hasRoleProduktAdmin,
                sortable: true,
                minWidth: 130,
                maxWidth: 150,
                rule: this.validationContext.current.Inmatningshandelssortiment,
                dataChanged: dataChanged
            },
            {
                headerName: "Mottagningsplats *",
                field: "mottagningsplats",
                filter: "agTextColumnFilter",
                filterParams: {
                    filterOptions: ["contains"],
                    maxNumConditions: 1,
                    textMatcher: ({ data, filterText }) => {
                        const value = `${data.mottagningsplats.mottagningsplatsnummer}: ${data.mottagningsplats.namn}`;
                        return value.toLowerCase().indexOf(filterText.toLowerCase()) >= 0;
                    }
                },
                editable: this.hasRoleProduktAdmin,
                sortable: true,
                sortComparator: (valueA: MottagningsplatsDTO, valueB: MottagningsplatsDTO) => {
                    if (valueA.mottagningsplatsnummer === valueB.mottagningsplatsnummer) {
                        return 0;
                    }
                    return (Number(valueA.mottagningsplatsnummer) > Number(valueB.mottagningsplatsnummer)) ? 1 : -1;
                },
                type: "selector",
                minWidth: 200,
                maxWidth: 250,
                values: this.mottagningsplatser,
                viewFormatter: (arg) => arg ? `${arg.mottagningsplatsnummer}: ${arg.namn}` : undefined,
                formatter: (arg) => arg,
                rule: this.validationContext.current.Mottagningsplats,
                dataChanged: dataChanged
            },
            {
                headerName: "Anskaffningsform",
                field: "anskaffningsform",
                editable: this.hasRoleProduktAdmin,
                filter: true,
                filterParams: {
                    filterOptions: ["contains"],
                    maxNumConditions: 1,
                    textMatcher: ({ data, filterText }) => data.anskaffningsform.some((x: string) => x.toLowerCase().indexOf(filterText.toLowerCase()) >= 0)
                },
                sortable: false,
                type: "multiselector",
                minWidth: 200,
                maxWidth: 250,
                values: of(Anskaffningsformer.AllaRegelverkViol3),
                formatter: (arg) => arg,
                viewFormatter: (arg) => arg ? arg.join(",") : undefined,
                dataChanged: dataChanged
            },
            {
                readonly: (row) => !row.anskaffningsform?.some((x: string) => Anskaffningsformer.isVirkesKop(x)),
                headerName: "Affärsform",
                field: "affarsform",
                editable: this.hasRoleProduktAdmin,
                filter: true,
                filterParams: {
                    filterOptions: ["contains"],
                    maxNumConditions: 1,
                    textMatcher: ({ data, filterText }) => data.affarsform.some((x: string) => x.toLowerCase().indexOf(filterText.toLowerCase()) >= 0)
                },
                sortable: false,
                type: "multiselector",
                minWidth: 200,
                maxWidth: 250,
                multiselectorBindLabel: "namn",
                multiselectorBindValue: "kod",
                values: of(this.affarsformer),
                viewFormatter: (arg) => arg ? arg.join(",") : undefined,
                dataChanged: dataChanged
            },
            {
                headerName: "Befraktare",
                field: "befraktare",
                values: this.befraktare,
                formatter: (arg) => arg ? arg : undefined,
                viewFormatter: (arg: BefraktareDto) => arg ? `${arg.namn} (${arg.huvudkod}-${arg.internummer})` : undefined,
                type: "selector",
                filter: true,
                filterParams: {
                    filterOptions: ["contains"],
                    maxNumConditions: 1,
                    textMatcher: ({ data, filterText }) => {
                        const value = `${data.befraktare.huvudkod.toLowerCase()}-${data.befraktare.internummer.toLowerCase()}`;
                        return value.indexOf(filterText.toLowerCase()) >= 0;
                    }
                },
                editable: this.hasRoleProduktAdmin,
                sortable: true,
                sortComparator: (valueA: BefraktareDto, valueB: BefraktareDto) => {
                    if (ObjectUtils.isNullOrUndefined(valueA) || ObjectUtils.isNullOrUndefined(valueB) || valueA.huvudkod === valueB.huvudkod) {
                        return 0;
                    }
                    return (Number(valueA.huvudkod) > Number(valueB.huvudkod)) ? 1 : -1;
                },
                minWidth: 120,
                maxWidth: 170,
                dataChanged: dataChanged,
                tooltipGenerator: (row: StyrtabellRedovisningshanvisningViewModel) =>
                    row.befraktare ? `${row.befraktare.namn} (${row.befraktare.huvudkod}-${row.befraktare.internummer})` : undefined
            },
            {
                headerName: "Avverkningsform",
                field: "avverkningsform",
                type: "selector",
                editable: this.hasRoleProduktAdmin,
                values: of(Avverkningsformer.Alla),
                formatter: (arg) => arg ? arg : undefined,
                viewFormatter: (arg) => arg ? arg.namn : undefined,
                filter: false,
                sortable: false,
                minWidth: 100,
                maxWidth: 200,
                dataChanged: dataChanged
            },
            {
                headerName: "MO/RO/PO",
                field: "treeselectorVerksamhetsomradeSelectItems",
                editable: this.hasRoleProduktAdmin,
                filter: false,
                sortable: false,
                type: "treeselector",
                minWidth: 100,
                maxWidth: 200,
                values: (arg: { treeselectorVerksamhetsomradeSelectItems: any }) => arg.treeselectorVerksamhetsomradeSelectItems,
                comparePredicate: (a, b) => a.value === b,
                viewFormatter: this.treeSelectorViewFormatter,
                dataChanged: dataChanged
            },
            {
                headerName: "LKD",
                field: "treeselectorLkdSelectItems",
                editable: this.hasRoleProduktAdmin,
                filter: false,
                sortable: false,
                type: "treeselector",
                values: (arg: { treeselectorLkdSelectItems: any }) => arg.treeselectorLkdSelectItems,
                viewFormatter: this.treeSelectorViewFormatter,
                comparePredicate: (a, b) => a.value === b,
                minWidth: 100,
                maxWidth: 200,
                dataChanged: dataChanged,
                tooltipGenerator: (row: StyrtabellRedovisningshanvisningViewModel) => this.treeSelectorViewFormatter(row.treeselectorLkdSelectItems)
            },
            {
                headerName: "Transportslag",
                field: "transportslag",
                type: "selector",
                editable: this.hasRoleProduktAdmin,
                values: of(Transportslag.allaMedTransport),
                viewFormatter: (arg) => arg,
                formatter: (arg) => arg,
                filter: false,
                sortable: false,
                minWidth: 50,
                maxWidth: 100,
                dataChanged: dataChanged
            },
            {
                headerName: "Kontraktstyp",
                field: "kontraktstyp",
                type: "selector",
                editable: this.hasRoleProduktAdmin,
                values: of(Forstaledskontraktstyp.alla),
                viewFormatter: (arg) => arg,
                formatter: (arg) => arg,
                filter: false,
                sortable: false,
                minWidth: 50,
                maxWidth: 100,
                dataChanged: dataChanged
            },
            {
                headerName: "RH*",
                readonly: (row) => row.ersattningsrad,
                field: "redovisningshanvisning",
                type: "selector",
                rule: this.validationContext.current.Redovisningshanvisning,
                editable: this.hasRoleProduktAdmin,
                filter: "agTextColumnFilter",
                filterParams: {
                    filterOptions: ["contains"],
                    maxNumConditions: 1,
                    textMatcher: ({ data, filterText }) => {
                        const value = `${data.redovisningshanvisning.identitet}: ${data.redovisningshanvisning.namn}`;
                        return value.toLowerCase().indexOf(filterText.toLowerCase()) >= 0;
                    }
                },
                sortable: true,
                sortComparator: (valueA: RedovisningshanvisningDto, valueB: RedovisningshanvisningDto) => {
                    if (valueA.identitet === valueB.identitet) {
                        return 0;
                    }
                    return (valueA.identitet > valueB.identitet) ? 1 : -1;
                },
                initialWidth: 200,
                minWidth: 250,
                values: (args:
                    {
                        mottagningsplats: { id: number };
                        inmatningshandelssortiment: string;
                        transportslag?: string;
                        kontraktstyp?: string;
                        befraktare: any;
                        redovisningshanvisning: { id: number };
                        anskaffningsform: string[];
                    }) =>
                    this.regelverkViol3Service.getFilteredRedovisningshanvisningar(
                        args.anskaffningsform,
                        args.mottagningsplats?.id,
                        args.befraktare,
                        args.inmatningshandelssortiment,
                        args.transportslag,
                        args.kontraktstyp),
                viewFormatter: (arg) => arg ? `${arg.identitet}: ${arg.namn}` : undefined,
                formatter: (arg) => arg,
                tooltipGenerator: (row: StyrtabellRedovisningshanvisningViewModel) => Viol3formatService.formatRedovisninghanvisningTooltip(row.redovisningshanvisning),
                dataChanged: dataChanged
            },
            {
                headerName: "Ersätt",
                field: "ersattningsrad",
                type: "checkbox",
                maxWidth: 80,
                readonly: !this.hasRoleProduktAdmin,
                dataChanged: dataChanged
            },
            {
                children: [
                    {
                        headerName: "Extra HS RH",
                        editable: this.hasRoleProduktAdmin,
                        field: "styrtabellRedovisningshanvisningar",
                        type: "text",
                        viewFormatter: (c: StyrtabellRedovisningshanvisningHandelssortimentDto[]) =>
                            (c?.map(x => `${x?.inmatningshandelssortiment ?? ""} ${x?.redovisningshanvisning?.identitet ?? ""}`) ?? []).join(",")
                    },
                    {
                        headerName: null,
                        editable: false,
                        disabled: !this.hasRoleProduktAdmin,
                        field: " ",
                        type: "button",
                        onClick: (rh: StyrtabellRedovisningshanvisningViewModel) => this.editExtraHsRh(rh),
                        buttonType: RaskColumnButtonTypes.edit,
                        text: "",
                        minWidth: 30,
                        maxWidth: 30
                    }
                ]
            }
        ] as unknown as RaskColumn[];
    }
}
